import React, { useState } from 'react';
import { Typography, Link, Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';

const ExpandableText = ({ text, limit = 250, isComment = false }) => {
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();

  if (!text) return null;

  const mentionRegex = /@\[([^\]]+)\]\((\d+)\)/g;
  const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+)/gi;
  const hashtagRegex = /#\[?(\w+)\]?/i;

  const handleMentionClick = (e, displayName, id) => {
    e.stopPropagation();
    const slug = displayName.toLowerCase().replace(/\s+/g, '');
    navigate(`/${slug}`, { state: { targetId: id } });
  };

  const handleHashtagClick = (e, tag) => {
    e.stopPropagation();
    navigate(`/search/top?q=%23${encodeURIComponent(tag)}`);
  };

  const renderContent = (content) => {
    const parts = content.split(/(@\[[^\]]+\]\(\d+\)|https?:\/\/[^\s]+|www\.[^\s]+|#\[?\w+\]?)/gi);

    return parts.map((part, i) => {
      if (!part) return null;

      const mentionMatch = mentionRegex.exec(part);
      if (mentionMatch) {
        const [_, displayName, id] = mentionMatch;
        mentionRegex.lastIndex = 0;
        return (
          <Typography
            key={i}
            component="span"
            onClick={(e) => handleMentionClick(e, displayName, id)}
            sx={{
              color: '#1877f2',
              fontWeight: 600,
              cursor: 'pointer',
              fontSize: isComment ? '0.85rem' : '0.93rem',
              '&:hover': { textDecoration: 'underline' }
            }}
          >
            {displayName}
          </Typography>
        );
      }

      if (part.match(urlRegex)) {
        const href = part.toLowerCase().startsWith('www.') ? `https://${part}` : part;
        return (
          <Link
            key={i}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            sx={{
              color: '#1877f2',
              textDecoration: 'none',
              fontWeight: 500,
              fontSize: isComment ? '0.85rem' : '0.93rem',
              '&:hover': { textDecoration: 'underline' }
            }}
          >
            {part}
          </Link>
        );
      }

      const hashtagMatch = part.match(hashtagRegex);
      if (hashtagMatch) {
        const tagName = hashtagMatch[1];
        return (
          <Typography
            key={i}
            component="span"
            onClick={(e) => handleHashtagClick(e, tagName)}
            sx={{
              color: '#3B207F',
              fontWeight: 700,
              cursor: 'pointer',
              fontSize: isComment ? '0.85rem' : '0.93rem',
              '&:hover': { textDecoration: 'underline', opacity: 0.8 }
            }}
          >
            #{tagName}
          </Typography>
        );
      }

      return <span key={i}>{part}</span>;
    });
  };

  const isLongText = text.length > limit;
  const displayedText = expanded || !isLongText ? text : `${text.substring(0, limit)}...`;

  return (
    <Box sx={{ px: isComment ? 0 : 2, pb: 1 }}>
      <Typography
        sx={{
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          fontSize: isComment ? '0.85rem' : '0.93rem',
          color: '#050505',
          lineHeight: 1.4,
          display: 'inline'
        }}
      >
        {renderContent(displayedText)}
      </Typography>

      {isLongText && (
        <Typography
          onClick={(e) => {
            e.stopPropagation();
            setExpanded(!expanded);
          }}
          sx={{
            color: '#65676b',
            cursor: 'pointer',
            fontWeight: 600,
            display: 'block',
            mt: 0.5,
            fontSize: isComment ? '0.8rem' : '0.85rem',
            '&:hover': { textDecoration: 'underline' }
          }}
        >
          {expanded ? 'Ver menos' : 'Ver más'}
        </Typography>
      )}
    </Box>
  );
};

export default ExpandableText;
